/**
 * Cache policy: the single place that decides what may be cached, under
 * which document id, and how stale is too stale.
 *
 * WHY a policy module rather than the rules living in each backend: the
 * limits here are enforced in three places that must agree. The local
 * backend, the Firebase backend and `firestore.rules` all apply the same
 * numbers, so a write the offline suite accepts is a write real Firestore
 * accepts. If you change a constant here, change the matching constant in
 * `firestore.rules`; the comment above the cache block there names this
 * file.
 *
 * WHY namespaces are an allowlist and not free strings: the cache is one
 * flat Firestore collection. If callers could invent namespaces, a typo or
 * a loop would quietly grow the collection forever and the security rules
 * would have nothing concrete to check against. Three namespaces exist
 * today, and adding a fourth is a deliberate edit to this array.
 *
 * WHY the payload is stored as a JSON string rather than a nested map:
 * Firestore rules cannot walk a nested structure, so a map payload could
 * carry a megabyte of anything and no rule could tell. A string has an
 * exact `size()`, which makes the document size cap in `firestore.rules`
 * real rather than decorative. The serialisation is invisible above the
 * backend contract: callers hand in an object and get the same object
 * back.
 *
 * TTL is deliberately NOT enforced here. A live game's schedule row goes
 * stale in seconds and a player's biography is good for weeks, so only the
 * caller can pick a sensible age. `readCache` returns `fetchedAt` for that
 * decision. What this module does enforce is a hard ceiling: past
 * `CACHE_HARD_MAX_AGE_MS` nothing is served, whatever the caller thinks.
 */

/**
 * The complete set of namespaces. Adding one is an edit to this line.
 *
 *   schedule  a week of fixtures, live or archived
 *   player    a profile or a club's squad
 *   season    which seasons exist, which one is being played now, and the
 *             week structure of one season. This is the namespace that
 *             makes past seasons worth keeping: a season that has finished
 *             never changes again, so the copy written while it was on is
 *             still the right answer years later.
 */
export const CACHE_NAMESPACES = Object.freeze(["schedule", "player", "season"]);

/**
 * Bumped when the meaning of a cached payload changes. A reader that sees
 * a version it does not recognise treats the entry as a miss rather than
 * guessing, so a schema change never surfaces as a mangled scoreboard.
 */
export const CACHE_SCHEMA_VERSION = 1;

/** Document ids are bounded so one caller cannot mint pathological ids. */
export const CACHE_MAX_KEY_LENGTH = 120;

/**
 * Roughly 120 KB of JSON. Generous for a week of fixtures or a player
 * profile, and small enough that even four-byte characters throughout stay
 * inside Firestore's 1 MiB document limit.
 */
export const CACHE_MAX_VALUE_LENGTH = 120000;

/** Provider ids are short slugs such as "nfl" or "mock". */
export const CACHE_MAX_PROVIDER_LENGTH = 40;

/**
 * The hard ceiling, applied on read no matter what TTL the caller wants.
 *
 * Two weeks: beyond that an NFL schedule has been reshuffled by flexing
 * and results, and a player entry may describe someone who has moved
 * team. Serving either would break the promise that this app never shows
 * something it cannot stand behind, so the storage layer refuses.
 */
export const CACHE_HARD_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Allowance for a browser clock that is slightly ahead of the server.
 * An entry stamped further into the future than this is treated as
 * unusable rather than as fresh forever.
 */
export const CACHE_CLOCK_SKEW_MS = 60 * 1000;

/**
 * How far back a WRITE may date itself.
 *
 * An entry becomes old by sitting in the collection, never by arriving
 * old, so a write claiming to be a fortnight old is either a broken clock
 * or someone trying to control what other readers consider stale.
 * `firestore.rules` enforces the same window, which is why it is a write
 * check and not a read check.
 */
export const CACHE_MAX_BACKDATE_MS = 24 * 60 * 60 * 1000;

/** The exact key set of a stored entry. Mirrored by `cacheFields()` in the rules. */
export const CACHE_ENTRY_FIELDS = Object.freeze([
  "namespace",
  "key",
  "value",
  "fetchedAt",
  "provider",
  "schemaVersion",
  "updatedBy",
]);

export function isCacheNamespace(namespace) {
  return CACHE_NAMESPACES.includes(namespace);
}

export function assertCacheNamespace(namespace) {
  if (!isCacheNamespace(namespace)) {
    throw new RangeError(
      `Unknown cache namespace "${namespace}". ` +
        `Allowed: ${CACHE_NAMESPACES.join(", ")}. ` +
        "Add it to CACHE_NAMESPACES in cachePolicy.js and to cacheNamespaces() in firestore.rules."
    );
  }
  return namespace;
}

/**
 * A tiny non-cryptographic fingerprint (FNV-1a, 32 bit).
 *
 * Used only to keep two long keys apart after truncation. It is not a
 * security primitive and nothing trusts it: the worst case of a collision
 * is that two cache entries share a slot and one is refetched.
 */
function fingerprint(input) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // The shifts are the standard FNV prime multiply, kept in 32 bits.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash.toString(36).padStart(7, "0");
}

/**
 * Turn any caller-supplied key into a safe, bounded, stable document id
 * fragment.
 *
 * Keys are case-insensitive and everything outside `a-z0-9` collapses to a
 * hyphen, which keeps a season string, an ESPN player id and a URL-ish key
 * all legal. An over-long key is truncated and given a fingerprint suffix
 * so two long keys that share a prefix do not share a cache slot.
 *
 * @param {string} key
 * @returns {string}
 */
export function sanitiseCacheKey(key) {
  const raw = String(key ?? "").trim().toLowerCase();
  const cleaned = raw.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  if (!cleaned) {
    throw new RangeError(
      "A cache key must contain at least one letter or digit."
    );
  }

  if (cleaned.length <= CACHE_MAX_KEY_LENGTH) return cleaned;

  const suffix = fingerprint(raw);
  return `${cleaned.slice(0, CACHE_MAX_KEY_LENGTH - suffix.length - 1)}-${suffix}`;
}

/**
 * The Firestore document id for one entry.
 *
 * Namespace and key are joined rather than nested so the whole cache is a
 * single collection with a single rule. The rules re-derive this id from
 * the document's own fields, which is what stops a "player" payload being
 * filed under a "schedule" id.
 */
export function cacheDocId(namespace, key) {
  assertCacheNamespace(namespace);
  return `${namespace}__${sanitiseCacheKey(key)}`;
}

/**
 * Serialise a payload, refusing anything JSON cannot represent or that
 * would breach the size cap. Failing here is much kinder than failing in
 * Firestore with a permission error that says nothing about why.
 */
export function serialiseCacheValue(value) {
  const json = JSON.stringify(value);

  if (typeof json !== "string") {
    throw new TypeError(
      "A cache value must be JSON-serialisable (undefined and functions are not)."
    );
  }

  if (json.length > CACHE_MAX_VALUE_LENGTH) {
    throw new RangeError(
      `Cache value is ${json.length} characters, over the ${CACHE_MAX_VALUE_LENGTH} limit. ` +
        "Cache a narrower slice rather than raising the cap: the limit is mirrored in firestore.rules."
    );
  }

  return json;
}

/**
 * Build the exact document both backends write. Every field is validated
 * here, so the two implementations cannot drift apart and neither can
 * drift away from the security rules.
 *
 * @param {object} args
 * @param {string} args.namespace   one of CACHE_NAMESPACES
 * @param {string} args.key         caller key, sanitised on the way in
 * @param {*} args.value            any JSON-serialisable payload
 * @param {string} args.provider    which provider produced the payload
 * @param {number} [args.fetchedAt] epoch ms, defaults to now
 * @param {string} args.updatedBy   uid of the writer
 */
export function buildCacheEntry({
  namespace,
  key,
  value,
  provider,
  fetchedAt,
  updatedBy,
}) {
  assertCacheNamespace(namespace);

  const providerId = String(provider ?? "").trim();
  if (!providerId || providerId.length > CACHE_MAX_PROVIDER_LENGTH) {
    throw new RangeError(
      "writeCache needs meta.provider: the id of whatever produced this payload, " +
        `1 to ${CACHE_MAX_PROVIDER_LENGTH} characters. It is recorded so a reader can tell ` +
        "real provider data from the practice game."
    );
  }

  const writer = String(updatedBy ?? "").trim();
  if (!writer) {
    throw new Error(
      "writeCache needs a writer uid. Firestore rules require the cache author to be signed in."
    );
  }

  const now = Date.now();
  const stamp = fetchedAt ?? now;
  if (typeof stamp !== "number" || !Number.isFinite(stamp) || stamp <= 0) {
    throw new RangeError("meta.fetchedAt must be a positive epoch-milliseconds number.");
  }
  if (stamp > now + CACHE_CLOCK_SKEW_MS || stamp < now - CACHE_MAX_BACKDATE_MS) {
    throw new RangeError(
      "meta.fetchedAt must be close to now: it records when this payload was " +
        "fetched, and firestore.rules pins it to the same window."
    );
  }

  return Object.freeze({
    namespace,
    key: sanitiseCacheKey(key),
    value: serialiseCacheValue(value),
    fetchedAt: stamp,
    provider: providerId,
    schemaVersion: CACHE_SCHEMA_VERSION,
    updatedBy: writer,
  });
}

/**
 * Validate a stored entry on the way out and hand back the caller's shape.
 *
 * WHY re-validate on read when the rules already validated on write: the
 * rules are the boundary for Firestore, but the local backend has no
 * boundary at all, a document may predate a schema change, and the whole
 * point of the threat model is that a client-writable cache cannot be
 * trusted on faith. Anything that fails a check is reported as a miss, so
 * the caller falls through to a live fetch rather than rendering rubbish.
 *
 * @returns {{value: *, fetchedAt: number, provider: string}|null}
 */
export function normaliseCacheRead(entry, now = Date.now()) {
  if (!entry || typeof entry !== "object") return null;

  const { namespace, value, fetchedAt, provider, schemaVersion } = entry;

  if (!isCacheNamespace(namespace)) return null;
  if (schemaVersion !== CACHE_SCHEMA_VERSION) return null;

  if (typeof fetchedAt !== "number" || !Number.isFinite(fetchedAt) || fetchedAt <= 0) {
    return null;
  }
  // Postdated: refuse it, otherwise a bad clock buys an entry immortality.
  if (fetchedAt > now + CACHE_CLOCK_SKEW_MS) return null;
  // The hard ceiling, applied whatever TTL the caller had in mind.
  if (now - fetchedAt > CACHE_HARD_MAX_AGE_MS) return null;

  if (
    typeof provider !== "string" ||
    provider.length === 0 ||
    provider.length > CACHE_MAX_PROVIDER_LENGTH
  ) {
    return null;
  }

  if (typeof value !== "string" || value.length > CACHE_MAX_VALUE_LENGTH) return null;

  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }

  return { value: parsed, fetchedAt, provider };
}
